import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { SubtopicOfFocusNode } from 'src/generated/webapp_gql';
import { useOutsideClick } from 'src/shared/hooks/useOutsideClick';

import { BodyTextXSmall, dsColors, HeadingText10, Icon } from 'src/shared/ds';
import { pxToRem } from 'src/shared/helpers/pxToRem';

const Category = styled.div({
    marginBottom: pxToRem(10),
    width: '100%',
    position: 'relative',
    border: `2px solid ${dsColors.lightGrey}`,
    borderRadius: pxToRem(5),
    paddingLeft: 0,
});

const SelectBoxWrapper = styled.div({
    padding: 0,
    cursor: 'pointer',
});

const SelectBoxHeaderWrapper = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: '100%',
    cursor: 'pointer',
});

const IconArrowWrapper = styled.div({
    display: 'flex',
    margin: pxToRem(15),
    marginRight: pxToRem(16),
});

const SelectBoxHeaderRow = styled.div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
});

const SelectBoxHeaderContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginLeft: pxToRem(8),
    paddingTop: pxToRem(8),
    paddingBottom: pxToRem(8),
});

const SelectBoxImageWrapper = styled.div({
    height: pxToRem(48),
    width: pxToRem(106),
    alignSelf: 'flex-start',
    display: 'flex',
});

const SelectBoxTopicImage = styled.img({
    height: '100%',
    objectFit: 'contain',
});

const CheckmarkWrapper = styled.span({
    paddingRight: pxToRem(8),
    display: 'flex',
});

const SelectBoxTopicTextWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    marginTop: pxToRem(6),
});

const DropDownWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    backgroundColor: 'white',
    width: '100',
    padding: pxToRem(16),
    paddingRight: 0,
});

const SelectedTopicWrapper = styled.div({
    display: 'flex',
});

const DropDownTextWrapper = styled.div({
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    marginBottom: pxToRem(15),
    display: 'flex',
});

interface Props {
    name: string;
    image: string;
    active: boolean;
    selectedItems: string[];
    subTopics: SubtopicOfFocusNode[];
    onClickBox: () => void;
    onClickOutsideBox: () => void;
    onClickSubtopic: (topic: string) => void;
}

export enum CategorySelectBoxTestId {
    SelectBoxWrapper = 'CategorySelectBoxTestId.SelectBoxWrapper',
    DropDownText = 'CategorySelectBoxTestId.DropDownText',
}

export const CategorySelectBox: React.FC<Props> = ({
    name,
    image,
    active,
    selectedItems,
    subTopics,
    onClickBox,
    onClickSubtopic,
    onClickOutsideBox,
}) => {
    const listRef = useRef(null);

    useOutsideClick(listRef, () => {
        if (active) {
            onClickOutsideBox();
        }
    });

    return (
        <Category ref={listRef}>
            <SelectBoxWrapper
                onClick={onClickBox}
                data-testid={CategorySelectBoxTestId.SelectBoxWrapper}
            >
                <SelectBoxHeaderWrapper>
                    <SelectBoxHeaderRow>
                        <SelectBoxImageWrapper>
                            <SelectBoxTopicImage src={image} />
                        </SelectBoxImageWrapper>
                        <SelectBoxHeaderContent>
                            <HeadingText10>{name}</HeadingText10>
                            {!active && selectedItems?.length > 0 && (
                                <SelectBoxTopicTextWrapper>
                                    {selectedItems?.map((topicUuid, i) => (
                                        <SelectedTopicWrapper key={topicUuid}>
                                            {selectedItems.includes(topicUuid) && (
                                                <CheckmarkWrapper>
                                                    <Icon name="checkmark" color="blue" />
                                                </CheckmarkWrapper>
                                            )}
                                            <BodyTextXSmall
                                                key={'selectbox-' + topicUuid + i}
                                                marginBottom={6}
                                            >
                                                {subTopics.find(topic => topic.uuid === topicUuid)
                                                    ?.name || ''}
                                            </BodyTextXSmall>
                                        </SelectedTopicWrapper>
                                    ))}
                                </SelectBoxTopicTextWrapper>
                            )}
                        </SelectBoxHeaderContent>
                    </SelectBoxHeaderRow>
                    <IconArrowWrapper>
                        <Icon name={active ? 'triangle-up' : 'triangle-down'} color="neutral400" />
                    </IconArrowWrapper>
                </SelectBoxHeaderWrapper>
            </SelectBoxWrapper>
            {active && (
                <DropDownWrapper>
                    {subTopics?.length &&
                        subTopics.map((subtopic, i) => (
                            <DropDownTextWrapper
                                key={'dropdown-' + subtopic.uuid + i}
                                data-testid={`CategorySelectBoxTestId.DropDownText${i}`}
                                onClick={() => onClickSubtopic(subtopic.uuid)}
                            >
                                {selectedItems.includes(subtopic.uuid) && (
                                    <CheckmarkWrapper>
                                        <Icon name="checkmark" color="blue" />
                                    </CheckmarkWrapper>
                                )}
                                <BodyTextXSmall
                                    fontColor={
                                        selectedItems.includes(subtopic.uuid)
                                            ? 'black'
                                            : 'neutral400'
                                    }
                                >
                                    {subtopic.name}
                                </BodyTextXSmall>
                            </DropDownTextWrapper>
                        ))}
                </DropDownWrapper>
            )}
        </Category>
    );
};
